import React, { useState, useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Award, BarChart3, FileBarChart, Table2, GraduationCap, Monitor, Headphones,
  PieChart, Brain, Search, Briefcase, AlertTriangle, GitCompareArrows, ShieldCheck,
  Crosshair, FlaskConical, Activity, Network, MessageSquare, Tags, CheckSquare,
  PenTool, FileSearch, Globe, BookOpen, Lock, History,
  ChevronDown, ChevronRight, Loader2, Maximize2, Sparkles, Plus, Trash2,
  Bot, Shield, Scale, Lightbulb, Download, Play, Pause, Volume2,
  Search as SearchIcon, X, FileText, Zap, Pin, PinOff, Clock, RefreshCw,
  TrendingUp, ListOrdered, Building, Map, Gauge, Edit3, Wrench, Target,
  LayoutGrid, CircleDot, Star, Heart, Flame, Bookmark, Coffee, Layers,
  Code, Database, Terminal, Cpu, Settings, Users, ArrowRightLeft, PanelRightClose
} from 'lucide-react';
import { toolsAPI, agentAPI, insightAPI, pinnedAPI, customToolsAPI } from '../api';
import ReportBuilderModal from './ReportBuilderModal';

const ICON_MAP = {
  'award': Award, 'file-bar-chart': FileBarChart, 'table': Table2, 'graduation-cap': GraduationCap,
  'presentation': Monitor, 'headphones': Headphones, 'pie-chart': PieChart, 'brain': Brain,
  'zap': Zap, 'search': Search, 'briefcase': Briefcase, 'alert-triangle': AlertTriangle,
  'git-compare': GitCompareArrows, 'shield-check': ShieldCheck, 'crosshair': Crosshair,
  'flask-conical': FlaskConical, 'activity': Activity, 'network': Network,
  'message-square': MessageSquare, 'tags': Tags, 'check-square': CheckSquare,
  'pen-tool': PenTool, 'file-search': FileSearch, 'globe': Globe, 'languages': Globe,
  'book-open': BookOpen, 'lock': Lock, 'history': History,
  'scale': Scale, 'trending-up': TrendingUp, 'list-ordered': ListOrdered,
  'building': Building, 'map': Map, 'gauge': Gauge,
  'sparkles': Sparkles, 'bot': Bot, 'lightbulb': Lightbulb,
  'target': Target, 'wrench': Wrench, 'edit': Edit3,
  'layout-grid': LayoutGrid, 'circle-dot': CircleDot, 'star': Star,
  'heart': Heart, 'flame': Flame, 'bookmark': Bookmark, 'coffee': Coffee,
  'layers': Layers, 'code': Code, 'database': Database, 'terminal': Terminal,
  'cpu': Cpu, 'settings': Settings, 'users': Users, 'arrow-right-left': ArrowRightLeft,
};

const CATEGORY_ICONS = {
  'file-text': FileText, 'brain': Brain, 'shield': Shield, 'target': Crosshair,
  'search': Search, 'list-checks': CheckSquare, 'globe': Globe, 'lock': Lock,
  'scale': Scale, 'building': Building, 'pen-tool': PenTool,
};

const ICON_PICKER_OPTIONS = [
  'sparkles', 'brain', 'zap', 'lightbulb', 'target', 'star', 'flame', 'heart',
  'award', 'shield-check', 'briefcase', 'scale', 'trending-up', 'gauge',
  'building', 'map', 'layers', 'wrench', 'settings', 'users',
  'code', 'database', 'terminal', 'cpu', 'globe', 'search',
  'pen-tool', 'edit', 'bookmark', 'coffee', 'network', 'activity',
  'file-bar-chart', 'pie-chart', 'table', 'list-ordered', 'tags', 'check-square',
];

const COLOR_OPTIONS = [
  '#DC2626', '#D97706', '#059669', '#0284C7', '#6590FF', '#6D28D9', '#7C3AED',
  '#BE185D', '#4338CA', '#0891B2', '#B45309', '#065F46',
];

export default function ToolsPanel({
  workspaceId,
  totalSources,
  onOpenFullscreen,
  existingSummary,
  existingMindMap,
  onGenSummary,
  onGenMindMap,
  summarizing,
  generatingMap,
  onClosePanel,
  workspaceLanguage,
}) {
  const [catalog, setCatalog] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});
  const [runningTools, setRunningTools] = useState(new Set());
  const [toolResults, setToolResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pinnedTools, setPinnedTools] = useState([]);

  // Agent state
  const [agentRuns, setAgentRuns] = useState([]);
  const [runningAgent, setRunningAgent] = useState(null);

  // Custom agents
  const [customAgents, setCustomAgents] = useState([]);
  const [agentDialog, setAgentDialog] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', description: '', system_prompt: '', icon: 'bot', color: '#6590FF' });

  // Custom tools
  const [customToolDialog, setCustomToolDialog] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [customToolForm, setCustomToolForm] = useState({ name: '', description: '', icon: 'sparkles', color: '#6590FF', prompt: '' });
  const [savingCustomTool, setSavingCustomTool] = useState(false);

  // Cached results / output popup
  const [cachedResults, setCachedResults] = useState([]);
  const [outputPopup, setOutputPopup] = useState(false);
  // ReportBuilder popup: opens before report_builder runs so the user can pick a
  // report type / language / description. `runTool('report_builder')` first opens
  // the modal; the modal then calls `runReportBuilder(options)` with the chosen params.
  const [reportBuilderOpen, setReportBuilderOpen] = useState(false);
  const [outputSearch, setOutputSearch] = useState('');

  const [toolsCollapsed, setToolsCollapsed] = useState(() => {
    try { return sessionStorage.getItem('bkw_tools_collapsed') !== '0'; } catch { return true; }
  });

  useEffect(() => {
    loadCatalog();
    loadAgentData();
    loadPinnedTools();
    loadCachedResults();
  }, [workspaceId]);

  // Refresh Recent Output after a Summary or Mind Map generation completes (success or failure).
  // The mindmap/summary endpoints persist their AgentRun rows server-side, but ToolsPanel keeps
  // its own copy of cached results in state — so we re-fetch when the parent signals a change.
  useEffect(() => {
    if (existingMindMap === null && existingSummary === null) return;
    loadCachedResults();
  }, [existingMindMap, existingSummary]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleToolsCollapsed = () => {
    setToolsCollapsed(prev => {
      const next = !prev;
      try { sessionStorage.setItem('bkw_tools_collapsed', next ? '1' : '0'); } catch (e) { console.error('Cache write error:', e); }
      return next;
    });
  };

  const loadCatalog = async () => {
    // Load from cache first for instant render
    try {
      const cached = sessionStorage.getItem('bkw_tools_catalog');
      if (cached) setCatalog(JSON.parse(cached));
    } catch (e) { console.error('Cache parse error:', e); }
    try {
      const res = await toolsAPI.catalog();
      setCatalog(res.data);
      try { sessionStorage.setItem('bkw_tools_catalog', JSON.stringify(res.data)); } catch (e) { console.error('Cache write error:', e); }
    } catch (e) { console.error(e); }
  };

  const loadAgentData = async () => {
    try {
      const [runs, custom] = await Promise.allSettled([
        agentAPI.listRuns(workspaceId),
        agentAPI.listCustom(workspaceId),
      ]);
      if (runs.status === 'fulfilled') setAgentRuns(runs.value.data);
      if (custom.status === 'fulfilled') setCustomAgents(custom.value.data);
    } catch (e) { console.error(e); }
  };

  const loadPinnedTools = async () => {
    // Load from cache first
    try {
      const cached = sessionStorage.getItem('bkw_pinned_tools');
      if (cached) setPinnedTools(JSON.parse(cached));
    } catch (e) { console.error('Cache parse error:', e); }
    try {
      const res = await pinnedAPI.get();
      setPinnedTools(res.data || []);
      try { sessionStorage.setItem('bkw_pinned_tools', JSON.stringify(res.data || [])); } catch (e) { console.error('Cache write error:', e); }
    } catch (e) { console.error(e); }
  };

  const loadCachedResults = async () => {
    // Hydrate from sessionStorage first so the Recent-Output strip renders instantly on
    // workspace re-entry. Background refresh below catches stale or in-flight runs.
    try {
      const cached = sessionStorage.getItem(`bkw_cached_results_${workspaceId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) setCachedResults(parsed);
      }
    } catch (e) { console.error('Cache parse error:', e); }
    try {
      const res = await toolsAPI.cachedResults(workspaceId);
      setCachedResults(res.data || []);
      try {
        sessionStorage.setItem(`bkw_cached_results_${workspaceId}`, JSON.stringify(res.data || []));
      } catch (e) { console.error('Cache write error:', e); }
    } catch (e) { console.error(e); }
  };

  const togglePin = async (toolId) => {
    const newPinned = pinnedTools.includes(toolId) ? pinnedTools.filter(t => t !== toolId) : [...pinnedTools, toolId];
    setPinnedTools(newPinned);
    try { sessionStorage.setItem('bkw_pinned_tools', JSON.stringify(newPinned)); } catch (e) { console.error('Cache write error:', e); }
    try { await pinnedAPI.set(newPinned); } catch (e) { console.error(e); }
  };

  const toggleCategory = (catId) => {
    setExpandedCats(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const startTool = (toolId) => setRunningTools(prev => new Set([...prev, toolId]));
  const endTool = (toolId) => setRunningTools(prev => { const s = new Set(prev); s.delete(toolId); return s; });

  const runTool = async (toolId) => {
    if (toolId === 'report_builder') {
      // Intercept: open the picker modal first; the actual run is triggered from the
      // modal's "Generate report" button which calls runReportBuilder() below.
      setReportBuilderOpen(true);
      return;
    }
    if (toolId === 'access_guard') {
      startTool(toolId);
      const pendingId = `pending-${Date.now()}`;
      setCachedResults(prev => [{ id: pendingId, type: `tool:${toolId}`, status: 'processing', started_at: new Date().toISOString(), result: null, tool_id: toolId }, ...prev]);
      try {
        const res = await toolsAPI.accessGuard(workspaceId);
        setCachedResults(prev => prev.filter(r => r.id !== pendingId));
        onOpenFullscreen({ type: 'access_guard', data: res.data });
      } catch (e) {
        setCachedResults(prev => prev.map(r => r.id === pendingId ? { ...r, status: 'failed', error: e.response?.data?.detail || e.message || 'Failed' } : r));
      }
      finally { endTool(toolId); }
      return;
    }
    if (toolId === 'audit_trail') {
      startTool(toolId);
      const pendingId = `pending-${Date.now()}`;
      setCachedResults(prev => [{ id: pendingId, type: `tool:${toolId}`, status: 'processing', started_at: new Date().toISOString(), result: null, tool_id: toolId }, ...prev]);
      try {
        const res = await toolsAPI.auditTrail(workspaceId);
        setCachedResults(prev => prev.filter(r => r.id !== pendingId));
        onOpenFullscreen({ type: 'audit_trail', data: res.data });
      } catch (e) {
        setCachedResults(prev => prev.map(r => r.id === pendingId ? { ...r, status: 'failed', error: e.response?.data?.detail || e.message || 'Failed' } : r));
      }
      finally { endTool(toolId); }
      return;
    }

    // Custom tool
    if (toolId.startsWith('custom_tool:')) {
      const realId = toolId.replace('custom_tool:', '');
      startTool(toolId);
      const pendingId = `pending-${Date.now()}`;
      setCachedResults(prev => [{ id: pendingId, type: `tool:${toolId}`, status: 'processing', started_at: new Date().toISOString(), result: null, tool_id: toolId }, ...prev]);
      try {
        const res = await customToolsAPI.run(workspaceId, realId, { document_ids: null });
        const result = { ...res.data, tool_id: toolId, timestamp: new Date().toISOString() };
        setToolResults(prev => [result, ...prev]);
        setCachedResults(prev => prev.map(r => r.id === pendingId ? { id: result.run_id, type: `tool:${toolId}`, status: 'completed', started_at: result.timestamp, result: result.result } : r));
        onOpenFullscreen({ type: 'tool_result', data: result });
      } catch (e) {
        setCachedResults(prev => prev.map(r => r.id === pendingId ? { ...r, status: 'failed', error: e.response?.data?.detail || e.message || 'Failed' } : r));
      }
      finally { endTool(toolId); }
      return;
    }

    startTool(toolId);
    const pendingId = `pending-${Date.now()}`;
    setCachedResults(prev => [{ id: pendingId, type: `tool:${toolId}`, status: 'processing', started_at: new Date().toISOString(), result: null, tool_id: toolId }, ...prev]);
    try {
      const res = await toolsAPI.run(workspaceId, toolId, { document_ids: null });
      const result = { ...res.data, tool_id: toolId, timestamp: new Date().toISOString() };
      setToolResults(prev => [result, ...prev]);
      setCachedResults(prev => prev.map(r => r.id === pendingId ? { id: result.run_id, type: `tool:${toolId}`, status: 'completed', started_at: result.timestamp, result: result.result } : r));
      onOpenFullscreen({ type: 'tool_result', data: result });
    } catch (e) {
      setCachedResults(prev => prev.map(r => r.id === pendingId ? { ...r, status: 'failed', error: e.response?.data?.detail || e.message || 'Failed' } : r));
    }
    finally { endTool(toolId); }
  };

  // ReportBuilder dedicated run path: receives the picker's `{report_type,
  // report_language, report_description}` and posts them alongside the standard
  // tool-run payload.  Mirrors the cached-results / fullscreen-open flow of `runTool`.
  const runReportBuilder = async ({ report_type, report_language, report_description }) => {
    const toolId = 'report_builder';
    startTool(toolId);
    const pendingId = `pending-${Date.now()}`;
    setCachedResults(prev => [{ id: pendingId, type: `tool:${toolId}`, status: 'processing', started_at: new Date().toISOString(), result: null, tool_id: toolId, report_type }, ...prev]);
    try {
      const res = await toolsAPI.run(workspaceId, toolId, { document_ids: null, report_type, report_language, report_description });
      const result = { ...res.data, tool_id: toolId, timestamp: new Date().toISOString() };
      setToolResults(prev => [result, ...prev]);
      setCachedResults(prev => prev.map(r => r.id === pendingId ? { id: result.run_id, type: `tool:${toolId}`, status: 'completed', started_at: result.timestamp, result: result.result } : r));
      onOpenFullscreen({ type: 'tool_result', data: result });
    } catch (e) {
      setCachedResults(prev => prev.map(r => r.id === pendingId ? { ...r, status: 'failed', error: e.response?.data?.detail || e.message || 'Failed' } : r));
      throw e;  // propagate so modal can surface the error
    }
    finally { endTool(toolId); }
  };

  const runBuiltInAgent = async (type) => {
    setRunningAgent(type);
    try {
      const res = await agentAPI.run(workspaceId, type, { document_ids: null });
      setAgentRuns(prev => [res.data, ...prev]);
      onOpenFullscreen({ type: 'agent', data: res.data });
    } catch (e) { console.error(e); }
    finally { setRunningAgent(null); }
  };

  const runCustomAgent = async (agentId) => {
    setRunningAgent(agentId);
    try {
      const res = await agentAPI.runCustom(workspaceId, agentId, { document_ids: null });
      setAgentRuns(prev => [res.data, ...prev]);
      onOpenFullscreen({ type: 'agent', data: res.data });
    } catch (e) { console.error(e); }
    finally { setRunningAgent(null); }
  };

  const createAgent = async () => {
    if (!newAgent.name.trim() || !newAgent.system_prompt.trim()) return;
    try {
      const res = await agentAPI.createCustom(workspaceId, newAgent);
      setCustomAgents(prev => [res.data, ...prev]);
      setNewAgent({ name: '', description: '', system_prompt: '', icon: 'bot', color: '#6590FF' });
      setAgentDialog(false);
    } catch (e) { console.error(e); }
  };

  // Custom tool CRUD
  const openCreateTool = () => {
    setEditingTool(null);
    setCustomToolForm({ name: '', description: '', icon: 'sparkles', color: '#6590FF', prompt: '' });
    setCustomToolDialog(true);
  };

  const openEditTool = (tool) => {
    setEditingTool(tool);
    setCustomToolForm({ name: tool.name, description: tool.description || '', icon: tool.icon, color: tool.color, prompt: tool.prompt || '' });
    setCustomToolDialog(true);
  };

  const saveCustomTool = async () => {
    if (!customToolForm.name.trim() || !customToolForm.prompt.trim()) return;
    setSavingCustomTool(true);
    try {
      if (editingTool) {
        await customToolsAPI.update(editingTool.id, customToolForm);
      } else {
        await customToolsAPI.create(customToolForm);
      }
      setCustomToolDialog(false);
      loadCatalog(); // Reload to pick up custom tools
    } catch (e) { console.error(e); }
    finally { setSavingCustomTool(false); }
  };

  const deleteCustomTool = async (toolId) => {
    try {
      const realId = toolId.replace('custom_tool:', '');
      await customToolsAPI.remove(realId);
      loadCatalog();
    } catch (e) { console.error(e); }
  };

  const filterTools = (tools) => {
    if (!searchQuery.trim()) return tools;
    const q = searchQuery.toLowerCase();
    return tools.filter(t => t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q));
  };

  const getIcon = (iconName, className = "w-4 h-4") => {
    const IconComp = ICON_MAP[iconName];
    return IconComp ? <IconComp className={className} /> : <Sparkles className={className} />;
  };

  const flatTools = {};
  catalog.forEach(cat => cat.tools.forEach(t => { flatTools[t.id] = { ...t, category: cat.category_name }; }));

  return (
    <>
      <div className="p-3 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Tools</h2>
          {onClosePanel && (
            <button
              onClick={onClosePanel}
              className="p-1.5 hover:bg-[#F3F4F6] rounded-lg transition-colors"
              data-testid="tools-panel-close-btn"
              title="Hide tools panel"
              aria-label="Hide tools panel"
            >
              <PanelRightClose className="w-4 h-4 text-[#4B5563]" />
            </button>
          )}
        </div>
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search tools..." className="w-full h-8 pl-8 pr-8 text-base border border-[#E5E7EB] rounded-xl bg-[#F9FAFB] focus:outline-none focus:border-[#6590FF] transition-colors" data-testid="tools-search" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-[#9CA3AF]" /></button>}
        </div>
      </div>

      <ScrollArea className="flex-1 w-full overflow-hidden">
        <div className="p-3 overflow-hidden w-[calc(416px-2px)]">
          {/* Featured Tools Row */}
          <div className="grid grid-cols-3 gap-2 mb-2 overflow-hidden">
            <FeaturedCard icon={<Sparkles className="w-7 h-7" />} label="Summary" color="#DC2626" hoverBorder="hover:border-[#DC2626]" hoverBg="hover:bg-red-50/50"
              onClick={onGenSummary} loading={summarizing} disabled={totalSources === 0} testId="tool-summary" />
            <FeaturedCard icon={<Network className="w-7 h-7" />} label="Mind Map" color="#059669" hoverBorder="hover:border-[#059669]" hoverBg="hover:bg-green-50/50"
              onClick={onGenMindMap} loading={generatingMap} disabled={totalSources === 0} testId="tool-mindmap" />
            <FeaturedCard icon={<Award className="w-7 h-7" />} label="Exec Brief" color="#6590FF" hoverBorder="hover:border-[#6590FF]" hoverBg="hover:bg-blue-50/50"
              onClick={() => runTool('executive_brief')} loading={runningTools.has('executive_brief')} disabled={totalSources === 0} testId="tool-executive-brief" />
          </div>

          {/* Favourites (Pinned Tools) */}
          {pinnedTools.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wider mb-1.5 px-1">Favourites</p>
              <div className="grid grid-cols-3 gap-2 overflow-hidden">
                {pinnedTools.map(tid => {
                  const t = flatTools[tid];
                  if (!t) return null;
                  return (
                    <FeaturedCard key={tid} icon={getIcon(t.icon, "w-6 h-6")} label={t.name} color={t.color}
                      hoverBorder={`hover:border-[${t.color}]`} hoverBg="hover:bg-gray-50/50"
                      onClick={() => runTool(tid)} loading={runningTools.has(tid)} disabled={totalSources === 0 && t.special !== 'dashboard'} testId={`pinned-${tid}`} />
                  );
                })}
              </div>
            </div>
          )}

          {/* Collapsible Tools List Toggle */}
          <button
            onClick={toggleToolsCollapsed}
            className="w-full flex items-center justify-between py-1.5 px-1.5 mb-1 hover:bg-[#F3F4F6] rounded-xl transition-colors text-sm font-bold text-[#6B7280] uppercase tracking-wider"
            data-testid="toggle-tools-collapse"
          >
            <span>{toolsCollapsed ? 'Show All Tools' : 'All Tools'}</span>
            {toolsCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {/* Tool Categories */}
          {!toolsCollapsed && catalog.map(cat => {
            const filtered = filterTools(cat.tools);
            if (filtered.length === 0 && searchQuery) return null;
            const isExpanded = expandedCats[cat.category_id];
            const CatIcon = CATEGORY_ICONS[cat.icon] || FileText;
            const isCustomCat = cat.category_id === 'custom_tools';

            return (
              <div key={cat.category_id} className="mb-0.5" data-testid={`tool-category-${cat.category_id}`}>
                <button onClick={() => toggleCategory(cat.category_id)}
                  className="w-full flex items-center gap-2 py-2 px-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors">
                  {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />}
                  <CatIcon className="w-[18px] h-[18px] text-[#4B5563]" />
                  <span className="text-base font-bold text-[#111827]">{cat.category_name}</span>
                  <span className="ml-auto text-xs text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{cat.tools.length}</span>
                </button>

                {isExpanded && (
                  <div className="ml-3 mt-0.5 space-y-0 mb-1 overflow-hidden">
                    {(searchQuery ? filtered : cat.tools).map(tool => (
                      <div key={tool.id} className="flex items-center group">
                        <button onClick={() => runTool(tool.id)}
                          disabled={runningTools.has(tool.id) || (totalSources === 0 && tool.special !== 'dashboard')}
                          className="flex-1 flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#F3F4F6] transition-all text-left disabled:opacity-40 min-w-0"
                          data-testid={`tool-${tool.id}`}>
                          {runningTools.has(tool.id) ? (
                            <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" style={{ color: tool.color }} />
                          ) : (
                            <span style={{ color: tool.color }} className="flex-shrink-0">
                              {getIcon(tool.icon, "w-5 h-5")}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-semibold text-[#111827] truncate">{tool.name}</p>
                            <p className="text-xs text-[#9CA3AF] truncate">{tool.desc}</p>
                          </div>
                        </button>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                          {tool.custom && (
                            <>
                              <button onClick={() => {
                                const realId = tool.id.replace('custom_tool:', '');
                                openEditTool({ id: realId, name: tool.name, description: tool.desc, icon: tool.icon, color: tool.color, prompt: '' });
                                // Load full tool data for editing
                                customToolsAPI.list().then(res => {
                                  const full = (res.data || []).find(t => t.id === realId);
                                  if (full) setCustomToolForm(prev => ({ ...prev, prompt: full.prompt }));
                                });
                              }}
                                className="p-1 hover:bg-[#EEF2FF] rounded-xl" title="Edit tool" data-testid={`edit-custom-tool-${tool.id}`}>
                                <Edit3 className="w-3.5 h-3.5 text-[#6590FF]" />
                              </button>
                              <button onClick={() => deleteCustomTool(tool.id)}
                                className="p-1 hover:bg-[#FEE2E2] rounded-xl" title="Delete tool" data-testid={`delete-custom-tool-${tool.id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
                              </button>
                            </>
                          )}
                          {!tool.custom && (
                            <button onClick={() => togglePin(tool.id)}
                              className="p-1 hover:bg-[#E5E7EB] rounded-xl" title={pinnedTools.includes(tool.id) ? 'Remove from favourites' : 'Add to favourites'} data-testid={`pin-${tool.id}`}>
                              {pinnedTools.includes(tool.id) ? <PinOff className="w-3.5 h-3.5 text-[#6590FF]" /> : <Pin className="w-3.5 h-3.5 text-[#9CA3AF]" />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Add new custom tool button (in custom tools category) */}
                    {isCustomCat && (
                      <button onClick={openCreateTool}
                        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#EEF2FF] transition-all text-left border border-dashed border-[#D1D5DB] hover:border-[#6590FF] mt-1"
                        data-testid="add-custom-tool-btn">
                        <Plus className="w-5 h-5 text-[#6590FF] flex-shrink-0" />
                        <span className="text-base font-medium text-[#6590FF]">Create Custom Tool</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* If no custom tools category exists yet, show standalone create button */}
          {!toolsCollapsed && !catalog.some(c => c.category_id === 'custom_tools') && (
            <div className="mb-0.5" data-testid="tool-category-custom_tools_empty">
              <button onClick={() => { toggleCategory('custom_tools_empty'); }}
                className="w-full flex items-center gap-2 py-2 px-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors">
                {expandedCats['custom_tools_empty'] ? <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />}
                <PenTool className="w-[18px] h-[18px] text-[#4B5563]" />
                <span className="text-base font-bold text-[#111827]">Custom Tools</span>
                <span className="ml-auto text-xs text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 rounded-full">0</span>
              </button>
              {expandedCats['custom_tools_empty'] && (
                <div className="ml-3 mt-0.5 mb-1">
                  <button onClick={openCreateTool}
                    className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#EEF2FF] transition-all text-left border border-dashed border-[#D1D5DB] hover:border-[#6590FF]"
                    data-testid="add-custom-tool-btn-empty">
                    <Plus className="w-5 h-5 text-[#6590FF] flex-shrink-0" />
                    <span className="text-base font-medium text-[#6590FF]">Create Custom Tool</span>
                  </button>
                </div>
              )}
            </div>
          )}

          <Separator className="my-2" />

          {/* Recent Output */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-[#6B7280] uppercase tracking-wider">Recent Output</h3>
            <button onClick={() => setOutputPopup(true)}
              className="text-sm font-medium text-[#6590FF] hover:underline flex items-center gap-0.5" data-testid="expand-output-btn">
              <Maximize2 className="w-3.5 h-3.5" /> All ({cachedResults.length})
            </button>
          </div>

          <div className="space-y-2 overflow-hidden min-w-0">
            {/* Show all cached results sorted by date, latest first */}
            {cachedResults.slice(0, 6).map((r, i) => {
              const typeKey = r.type || '';
              const toolId = r.tool_id || typeKey.replace('tool:', '');

              // Processing state
              if (r.status === 'processing') {
                const info = flatTools[toolId];
                return (
                  <div key={r.id || i} className="p-3 bg-[#F9FAFB] border border-[#6590FF]/20 rounded-xl overflow-hidden min-w-0 animate-pulse" data-testid={`output-processing-${toolId}`}>
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-[#6590FF] animate-spin flex-shrink-0" />
                      <span className="text-sm font-bold text-[#6590FF] capitalize truncate">{info?.name || toolId.replace(/_/g, ' ')}</span>
                      <span className="ml-auto text-[11px] text-[#9CA3AF]">Processing...</span>
                    </div>
                  </div>
                );
              }

              // Failed state
              if (r.status === 'failed') {
                const info = flatTools[toolId];
                return (
                  <div key={r.id || i} className="p-3 bg-[#FEF2F2] border border-[#C8102E]/20 rounded-xl overflow-hidden min-w-0" data-testid={`output-failed-${toolId}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertTriangle className="w-4 h-4 text-[#C8102E] flex-shrink-0" />
                      <span className="text-sm font-bold text-[#C8102E] capitalize truncate">{info?.name || toolId.replace(/_/g, ' ')}</span>
                      <span className="ml-auto text-[11px] font-semibold text-[#C8102E] uppercase">Failed</span>
                    </div>
                    <p className="text-xs text-[#C8102E]/70 mb-2 line-clamp-2">{r.error || 'An error occurred'}</p>
                    <button onClick={() => { setCachedResults(prev => prev.filter(x => x.id !== r.id)); runTool(toolId); }}
                      className="flex items-center gap-1 text-xs font-semibold text-[#6590FF] hover:underline"
                      data-testid={`retry-tool-${toolId}`}>
                      <RefreshCw className="w-3 h-3" /> Retry
                    </button>
                  </div>
                );
              }

              const isMindMap = typeKey === 'tool:mindmap';
              const isSummary = typeKey === 'tool:summary';
              const isAgent = typeKey.startsWith('agent:') || ['compliance','conflict','recommendation'].some(a => typeKey.includes(a));
              const isCustomTool = typeKey.startsWith('tool:custom_tool:');
              const typeLabel = typeKey.replace('tool:', '').replace('custom_tool:', '').replace(/_/g, ' ');
              const info = flatTools[typeKey.replace('tool:', '')];

              if (isMindMap && r.result?.branches) {
                return (
                  <OutputCard key={r.id || i} icon={<Network className="w-4 h-4 text-[#059669]" />} title="Mind Map"
                    onExpand={() => onOpenFullscreen({ type: 'mindmap', data: r.result })}>
                    <p className="text-sm text-[#6590FF] font-semibold truncate">{r.result.central_topic}</p>
                  </OutputCard>
                );
              }

              if (isSummary && r.result?.summary) {
                return (
                  <OutputCard key={r.id || i} icon={<Sparkles className="w-4 h-4 text-[#DC2626]" />} title="Summary"
                    onExpand={() => onOpenFullscreen({ type: 'summary', data: r.result })}>
                    <div className="prose prose-sm max-w-none text-[#4B5563] text-sm leading-relaxed line-clamp-3 overflow-hidden">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.result.summary}</ReactMarkdown>
                    </div>
                  </OutputCard>
                );
              }

              const iconEl = isAgent
                ? (typeKey.includes('compliance') ? <Shield className="w-4 h-4 text-[#6590FF]" /> :
                   typeKey.includes('conflict') ? <Scale className="w-4 h-4 text-[#DC2626]" /> :
                   <Lightbulb className="w-4 h-4 text-[#059669]" />)
                : info ? <span style={{ color: info.color }}>{getIcon(info.icon || 'sparkles', 'w-4 h-4')}</span>
                : <Sparkles className="w-4 h-4 text-[#6B7280]" />;

              return (
                <OutputCard key={r.id || i} icon={iconEl}
                  title={info?.name || typeLabel || 'Output'}
                  onExpand={() => onOpenFullscreen({ type: r.type?.startsWith('tool:') ? 'tool_result' : 'agent', data: { ...r, tool_id: r.type?.replace('tool:', ''), result: r.result } })}>
                  {r.result?.audio_base64 && <AudioPlayer base64={r.result.audio_base64} />}
                  {r.result?.download_file && <DownloadBtn filename={r.result.download_file} label="Download PPTX" />}
                  <p className="text-sm text-[#4B5563] line-clamp-2 truncate">{r.result?.summary || r.result?.executive_summary || r.result?.title || 'Analysis complete'}</p>
                </OutputCard>
              );
            })}
          </div>

          {cachedResults.length === 0 && (
            <div className="py-4 text-center">
              <Sparkles className="w-6 h-6 text-[#D1D5DB] mx-auto mb-1.5" />
              <p className="text-sm text-[#6B7280]">Output will appear here</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Custom Tool Create/Edit Dialog */}
      {customToolDialog && (
        <CustomToolDialog
          isEditing={!!editingTool}
          form={customToolForm}
          setForm={setCustomToolForm}
          onSave={saveCustomTool}
          onClose={() => setCustomToolDialog(false)}
          saving={savingCustomTool}
          getIcon={getIcon}
        />
      )}

      {/* All Outputs Popup */}
      {outputPopup && (
        <AllOutputsPopup
          cachedResults={cachedResults}
          outputSearch={outputSearch}
          setOutputSearch={setOutputSearch}
          flatTools={flatTools}
          getIcon={getIcon}
          onOpenFullscreen={onOpenFullscreen}
          onClose={() => setOutputPopup(false)}
        />
      )}
      {reportBuilderOpen && (
        <ReportBuilderModal
          workspaceId={workspaceId}
          defaultLanguage={workspaceLanguage}
          onClose={() => setReportBuilderOpen(false)}
          onRun={runReportBuilder}
        />
      )}
    </>
  );
}

// ---- Custom Tool Create/Edit Dialog ----
function CustomToolDialog({ isEditing, form, setForm, onSave, onClose, saving, getIcon }) {
  const [showIconPicker, setShowIconPicker] = useState(false);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="custom-tool-dialog-overlay">
      <div className="bg-white w-full max-w-lg max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[#6590FF]" />
            <span className="text-base font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>{isEditing ? 'Edit Custom Tool' : 'Create Custom Tool'}</span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl" data-testid="close-custom-tool-dialog">
            <X className="w-4 h-4 text-[#4B5563]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Name + Icon Row */}
          <div className="flex gap-3">
            {/* Icon Picker */}
            <div className="relative">
              <label className="text-sm font-medium text-[#4B5563] mb-1.5 block">Icon</label>
              <button
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-12 h-10 border border-[#E5E7EB] rounded-xl flex items-center justify-center hover:border-[#6590FF] transition-colors"
                style={{ color: form.color }}
                data-testid="icon-picker-btn"
              >
                {getIcon(form.icon, "w-5 h-5")}
              </button>
              {showIconPicker && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-[#E5E7EB] rounded-xl shadow-xl p-3 w-[280px]" data-testid="icon-picker-dropdown">
                  <p className="text-[11px] font-bold text-[#6B7280] uppercase mb-2">Choose Icon</p>
                  <div className="grid grid-cols-8 gap-1 mb-3 max-h-[120px] overflow-y-auto">
                    {ICON_PICKER_OPTIONS.map(ic => (
                      <button key={ic} onClick={() => { setForm({ ...form, icon: ic }); setShowIconPicker(false); }}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${form.icon === ic ? 'bg-[#6590FF]/10 ring-2 ring-[#6590FF]' : 'hover:bg-[#F3F4F6]'}`}
                        data-testid={`icon-option-${ic}`}>
                        {getIcon(ic, "w-4 h-4")}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] font-bold text-[#6B7280] uppercase mb-2">Color</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {COLOR_OPTIONS.map(c => (
                      <button key={c} onClick={() => setForm({ ...form, color: c })}
                        className={`w-6 h-6 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-1 ring-[#111827] scale-110' : 'hover:scale-110'}`}
                        style={{ backgroundColor: c }}
                        data-testid={`color-option-${c}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Name */}
            <div className="flex-1">
              <label className="text-sm font-medium text-[#4B5563] mb-1.5 block">Tool Name <span className="text-[#DC2626]">*</span></label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Contract Analyzer"
                className="w-full h-10 px-3 text-base border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20"
                data-testid="custom-tool-name"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-[#4B5563] mb-1.5 block">Description</label>
            <input
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Short description of what this tool does"
              className="w-full h-10 px-3 text-base border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20"
              data-testid="custom-tool-description"
            />
          </div>

          {/* Prompt */}
          <div>
            <label className="text-sm font-medium text-[#4B5563] mb-1.5 block">Tool Prompt <span className="text-[#DC2626]">*</span></label>
            <p className="text-[11px] text-[#9CA3AF] mb-2">Define what this tool does. The documents will be provided as context automatically. Use "Return JSON:" to structure the output.</p>
            <textarea
              value={form.prompt}
              onChange={e => setForm({ ...form, prompt: e.target.value })}
              placeholder={`Example:\nAnalyze the provided documents and extract all contract terms, deadlines, and obligations.\n\nReturn JSON:\n{\n  "summary": "Overview",\n  "terms": [{"term": "text", "deadline": "date", "party": "who"}],\n  "risks": [{"risk": "text", "severity": "high|medium|low"}]\n}`}
              rows={8}
              className="w-full px-3 py-2.5 text-base border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20 resize-none font-mono text-sm leading-relaxed"
              data-testid="custom-tool-prompt"
            />
          </div>

          {/* Preview */}
          <div className="p-3 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl">
            <p className="text-[11px] font-bold text-[#6B7280] uppercase mb-2">Preview</p>
            <div className="flex items-center gap-2.5">
              <span style={{ color: form.color }} className="flex-shrink-0">
                {getIcon(form.icon, "w-5 h-5")}
              </span>
              <div className="min-w-0">
                <p className="text-base font-semibold text-[#111827] truncate">{form.name || 'Tool Name'}</p>
                <p className="text-xs text-[#9CA3AF] truncate">{form.description || 'Tool description'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5E7EB] px-5 py-3 flex gap-2">
          <button
            onClick={onSave}
            disabled={saving || !form.name.trim() || !form.prompt.trim()}
            className="flex-1 h-10 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-base font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-custom-tool-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : isEditing ? 'Save Changes' : 'Create Tool'}
          </button>
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E7EB] text-base font-medium text-[#4B5563] rounded-xl hover:bg-[#F3F4F6] transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function FeaturedCard({ icon, label, color, hoverBorder, hoverBg, onClick, loading, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={loading || disabled}
      className={`flex flex-col items-center justify-center gap-2 p-3 border border-[#E5E7EB] rounded-xl ${hoverBorder || ''} ${hoverBg || ''} transition-all disabled:opacity-40 min-w-0 overflow-hidden`}
      data-testid={testId}>
      {loading ? <Loader2 className="w-7 h-7 animate-spin" style={{ color }} /> : <span style={{ color }}>{icon}</span>}
      <span className="text-sm font-semibold text-[#4B5563] truncate w-full text-center">{label}</span>
    </button>
  );
}

function AgentBtn({ icon, label, color, onClick, loading, disabled, testId, onDelete }) {
  return (
    <div className="flex items-center gap-1 group">
      <button onClick={onClick} disabled={loading || disabled}
        className="flex-1 flex items-center gap-2.5 px-2.5 py-2 border border-[#E5E7EB] rounded-xl hover:border-current transition-colors disabled:opacity-40 text-left"
        style={{ color }} data-testid={testId}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
        <span className="text-base font-medium text-[#111827]">{label}</span>
      </button>
      {onDelete && (
        <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#FEE2E2] rounded-xl transition-all">
          <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
        </button>
      )}
    </div>
  );
}

function OutputCard({ icon, title, onExpand, children }) {
  return (
    <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl group overflow-hidden min-w-0" data-testid={`output-${title}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="text-sm font-bold text-[#111827] capitalize truncate">{title}</span>
        </div>
        <button onClick={onExpand} className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-[#E5E7EB] rounded-xl transition-all flex-shrink-0">
          <Maximize2 className="w-3.5 h-3.5 text-[#4B5563]" />
        </button>
      </div>
      <div className="overflow-hidden min-w-0">{children}</div>
      <button onClick={onExpand} className="mt-1.5 text-xs font-medium text-[#6590FF] hover:underline">View full output</button>
    </div>
  );
}

function AudioPlayer({ base64 }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    if (!audioRef.current) { audioRef.current = new Audio(`data:audio/mp3;base64,${base64}`); audioRef.current.onended = () => setPlaying(false); }
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };
  return (
    <button onClick={toggle} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#BE185D]/10 border border-[#BE185D]/20 rounded-xl mb-1.5 text-sm text-[#BE185D] font-medium hover:bg-[#BE185D]/20 transition-colors" data-testid="audio-player-btn">
      {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {playing ? 'Pause' : 'Play Audio'}
    </button>
  );
}

function DownloadBtn({ filename, label }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  return (
    <a href={`${API_URL}/api/tools/download/${filename}`} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#0891B2]/10 border border-[#0891B2]/20 rounded-xl mb-1.5 text-sm text-[#0891B2] font-medium hover:bg-[#0891B2]/20 transition-colors" data-testid="download-pptx-btn">
      <Download className="w-3.5 h-3.5" /> {label}
    </a>
  );
}

function AllOutputsPopup({ cachedResults, outputSearch, setOutputSearch, flatTools, getIcon, onOpenFullscreen, onClose }) {
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const filtered = cachedResults.filter(r => !outputSearch || (r.type || '').toLowerCase().includes(outputSearch.toLowerCase()) ||
    (flatTools[(r.type || '').replace('tool:', '')]?.name || '').toLowerCase().includes(outputSearch.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(0, page * PAGE_SIZE);

  const getTypeLabel = (r) => {
    const t = r.type || '';
    if (t === 'tool:mindmap') return 'Mind Map';
    if (t === 'tool:summary') return 'Summary';
    const info = flatTools[t.replace('tool:', '')];
    if (info) return info.name;
    return t.replace('tool:', '').replace('custom_tool:', '').replace('custom:', '').replace(/_/g, ' ');
  };

  const handleClick = (r) => {
    onClose();
    const t = r.type || '';
    if (t === 'tool:mindmap' && r.result?.branches) {
      onOpenFullscreen({ type: 'mindmap', data: r.result });
    } else if (t === 'tool:summary' && r.result?.summary) {
      onOpenFullscreen({ type: 'summary', data: r.result });
    } else {
      onOpenFullscreen({ type: t.startsWith('tool:') ? 'tool_result' : 'agent', data: { ...r, tool_id: t.replace('tool:', ''), result: r.result } });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="output-popup-overlay">
      <div className="bg-white w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5">
          <span className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>All Outputs ({filtered.length})</span>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl"><X className="w-4 h-4 text-[#4B5563]" /></button>
        </div>
        <div className="px-5 py-3 border-b border-[#E5E7EB]">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input type="text" value={outputSearch} onChange={e => { setOutputSearch(e.target.value); setPage(1); }}
              placeholder="Search outputs..." className="w-full h-9 pl-9 pr-3 text-base border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF]" data-testid="output-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {paged.map((r, i) => {
            const isMindMap = r.type === 'tool:mindmap';
            const isSummary = r.type === 'tool:summary';
            const isAgent = !r.type?.startsWith('tool:');
            const iconEl = isMindMap ? <Network className="w-4 h-4 text-[#059669]" />
              : isSummary ? <Sparkles className="w-4 h-4 text-[#DC2626]" />
              : isAgent ? <Shield className="w-4 h-4 text-[#6590FF]" />
              : <Sparkles className="w-4 h-4 text-[#6B7280]" />;
            return (
              <button key={r.id || i} onClick={() => handleClick(r)}
                className="w-full flex items-center gap-3 p-3 hover:bg-[#F3F4F6] rounded-xl transition-colors text-left mb-1.5 border border-transparent hover:border-[#E5E7EB]"
                data-testid={`cached-result-${i}`}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-[#F3F4F6]">
                  {iconEl}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-[#111827] capitalize truncate">{getTypeLabel(r)}</p>
                  <p className="text-sm text-[#9CA3AF]">{r.started_at ? new Date(r.started_at).toLocaleString() : ''}</p>
                </div>
                <Maximize2 className="w-4 h-4 text-[#9CA3AF]" />
              </button>
            );
          })}
          {paged.length < filtered.length && (
            <button onClick={() => setPage(p => p + 1)}
              className="w-full py-2.5 text-base font-medium text-[#6590FF] hover:bg-[#F3F4F6] rounded-xl transition-colors"
              data-testid="load-more-outputs">
              Load more ({filtered.length - paged.length} remaining)
            </button>
          )}
          {filtered.length === 0 && <p className="text-base text-[#9CA3AF] text-center py-8">No outputs found</p>}
        </div>
      </div>
    </div>
  );
}

